import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { brl, pct } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { Pnl } from "@/types";

interface Props {
  pnl: Pnl;
  precoCompra: string;
  precoVenda: string;
  volume: string;
  onAlterar: (campo: "precoCompra" | "precoVenda" | "volume", valor: string) => void;
  somenteLeitura?: boolean;
}

const VARIANTE = { verde: "ok", ambar: "atencao", azul: "info" } as const;

function Indicador({
  rotulo,
  valor,
  detalhe,
  destaque,
}: {
  rotulo: string;
  valor: string;
  detalhe: React.ReactNode;
  destaque?: string;
}) {
  return (
    <div className="group relative overflow-hidden rounded-xl border border-border/70 bg-secondary/50 p-4 transition-all duration-200 ease-suave hover:-translate-y-px hover:bg-card hover:shadow-media">
      {/* Fio de acento que acende no hover — dá vida sem pedir atenção parado. */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px] scale-y-0 bg-primary transition-transform duration-200 ease-suave group-hover:scale-y-100"
      />
      <div className="rotulo-seco mb-1.5">{rotulo}</div>
      <div className={cn("numerico text-[1.35rem] font-extrabold leading-none text-fs-escuro", destaque)}>
        {valor}
      </div>
      <div className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{detalhe}</div>
    </div>
  );
}

export function PnlCard({
  pnl,
  precoCompra,
  precoVenda,
  volume,
  onAlterar,
  somenteLeitura,
}: Props) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>P&amp;L e equalização de ICMS</CardTitle>
          <Badge variant={VARIANTE[pnl.cor]}>{pnl.veredito}</Badge>
        </div>
        <CardDescription>
          Rota produtor → ECE → distribuidora do desenho. ST, frete, armazenagem e regimes especiais
          ficam fora — valide com o fiscal.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label htmlFor="preco-compra">Preço de compra (R$/m³)</Label>
            <Input
              id="preco-compra"
              inputMode="decimal"
              value={precoCompra}
              disabled={somenteLeitura}
              onChange={(e) => onAlterar("precoCompra", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="preco-venda">Preço de venda (R$/m³)</Label>
            <Input
              id="preco-venda"
              inputMode="decimal"
              value={precoVenda}
              disabled={somenteLeitura}
              onChange={(e) => onAlterar("precoVenda", e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="volume">Volume (m³)</Label>
            <Input
              id="volume"
              inputMode="decimal"
              value={volume}
              disabled={somenteLeitura}
              onChange={(e) => onAlterar("volume", e.target.value)}
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Indicador
            rotulo="ICMS na compra (crédito)"
            valor={pnl.compraDiferida ? "—" : `${brl(pnl.credito)} /m³`}
            detalhe={
              pnl.detalheCompra ??
              (pnl.compraDiferida
                ? "Aquisição interna de anidro — diferida"
                : `${pct(pnl.aliqCompra)} sobre o preço de compra`)
            }
          />
          <Indicador
            rotulo="ICMS na venda (débito)"
            valor={pnl.vendaDiferida ? "—" : `${brl(pnl.debito)} /m³`}
            detalhe={
              pnl.detalheVenda ??
              (pnl.vendaDiferida
                ? "Saída diferida (sem destaque)"
                : `${pct(pnl.aliqVenda)} sobre o preço de venda`)
            }
          />
          <Indicador
            rotulo="Saldo de ICMS"
            valor={`${brl(pnl.saldo)} /m³`}
            destaque={pnl.saldo > 0 ? "text-amber-700" : "text-cyan-800"}
            detalhe={
              <>
                {pnl.saldo > 0
                  ? "A recolher na operação"
                  : pnl.saldo < 0
                    ? "Crédito acumulado"
                    : "Neutro"}
                {pnl.volume > 0 && (
                  <>
                    {" · total "}
                    <b className="numerico">{brl(pnl.saldoTotal)}</b>
                  </>
                )}
              </>
            }
          />
          <Indicador
            rotulo="Margem líquida de ICMS"
            valor={`${brl(pnl.margemLiqIcms)} /m³`}
            destaque={pnl.margemLiqIcms >= 0 ? "text-emerald-800" : "text-destructive"}
            detalhe={
              <>
                Bruta {brl(pnl.margemBruta)} − saldo ICMS
                {pnl.volume > 0 && (
                  <>
                    {" · total "}
                    <b className="numerico">{brl(pnl.margemTotal)}</b>
                  </>
                )}
              </>
            }
          />
        </div>

        <p className="text-xs leading-relaxed text-muted-foreground">
          O ICMS é &ldquo;por dentro&rdquo;: o crédito é o imposto destacado na NF de venda do
          produtor e o débito, o destacado na NF de revenda da ECE. Para anidro, a aquisição interna
          pela comercializadora é diferida (sem crédito), assim como a venda para distribuidora (sem
          débito) — Convênio ICMS 15/23.
        </p>
      </CardContent>
    </Card>
  );
}
